import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import {
  FileText, ShoppingCart, Loader2, RefreshCw, CheckCircle2, Download,
  CreditCard, Sparkles, ChevronRight, ArrowLeft, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import DOMPurify from 'dompurify';
import { Input } from '../components/ui/input';

const TIER_COLORS = { basic: 'border-gray-200', standard: 'border-[#D4A843] ring-1 ring-[#D4A843]/10', premium: 'border-amber-300 ring-1 ring-amber-200' };

export default function DocForge() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('DocForge');
  const [view, setView] = useState('store');
  const [templates, setTemplates] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [selectedTier, setSelectedTier] = useState('standard');
  const [answers, setAnswers] = useState({});
  const [generating, setGenerating] = useState(false);
  const [generatedOrder, setGeneratedOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [tRes, oRes, sRes] = await Promise.all([
        api.get('/api/docforge/templates'),
        api.get('/api/docforge/orders'),
        api.get('/api/docforge/stats'),
      ]);
      setTemplates(tRes.data.templates || []);
      setOrders(oRes.data.orders || []);
      setStats(sRes.data);
    } catch (e) { /* handled */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async () => {
    if (!selected) return;
    const missing = selected.intake_fields.filter(f => f.required && !answers[f.id]);
    if (missing.length) { toast.error(`Fill required fields: ${missing.map(f => f.label).join(', ')}`); return; }
    setGenerating(true);
    try {
      const { data } = await api.post('/api/docforge/generate', { template_id: selected.id, tier: selectedTier, answers });
      toast.info('Document generating... this may take 30-60 seconds');
      // Poll for completion
      const orderId = data.id;
      const poll = setInterval(async () => {
        try {
          const { data: order } = await api.get(`/api/docforge/orders/${orderId}`);
          if (order.status === 'completed') {
            clearInterval(poll);
            setGeneratedOrder(order);
            setOrders(prev => [{ ...order, content: undefined }, ...prev.filter(o => o.id !== orderId)]);
            setGenerating(false);
            toast.success('Document generated!');
          } else if (order.status === 'failed') {
            clearInterval(poll);
            setGenerating(false);
            toast.error('Generation failed: ' + (order.error || 'Unknown error'));
          }
        } catch (e) { /* handled */ }
      }, 3000);
      // Safety timeout after 3 minutes
      setTimeout(() => { clearInterval(poll); setGenerating(false); }, 180000);
    } catch { toast.error('Generation failed'); setGenerating(false); }
  };

  const viewOrder = async (orderId) => {
    try {
      const { data } = await api.get(`/api/docforge/orders/${orderId}`);
      setGeneratedOrder(data);
      setView('preview');
    } catch { toast.error('Failed to load order'); }
  };

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="docforge-page">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="space-y-6 animate-fade-in">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2">
                <FileText className="w-6 h-6 text-[#D4A843]" /> DocForge
              </h1>
              <p className="text-sm text-gray-500 mt-1">AI-powered compliance & governance document generator</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant={view === 'store' ? 'default' : 'outline'} onClick={() => { setView('store'); setSelected(null); setGeneratedOrder(null); }} className="rounded-lg text-xs" data-testid="docforge-tab-store">Templates</Button>
              <Button size="sm" variant={view === 'orders' ? 'default' : 'outline'} onClick={() => { setView('orders'); setSelected(null); setGeneratedOrder(null); }} className="rounded-lg text-xs" data-testid="docforge-tab-orders">My Orders ({orders.length})</Button>
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white border border-gray-200 rounded-lg p-4 text-center hover-lift"><p className="text-2xl font-bold text-gray-900">{stats.total_orders}</p><p className="text-[10px] text-gray-500">Total Orders</p></div>
              <div className="bg-white border border-gray-200 rounded-lg p-4 text-center hover-lift"><p className="text-2xl font-bold text-emerald-600">{stats.completed}</p><p className="text-[10px] text-emerald-500">Completed</p></div>
              <div className="bg-white border border-gray-200 rounded-lg p-4 text-center hover-lift"><p className="text-2xl font-bold text-[#D4A843]">${stats.total_revenue}</p><p className="text-[10px] text-[#D4A843]">Revenue</p></div>
            </div>
          )}

          {/* Template Store */}
          {view === 'store' && !selected && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="docforge-template-grid">
              {templates.map(t => (
                <div key={t.id} className="bg-white border border-gray-200 rounded-lg p-5 hover-lift cursor-pointer" onClick={() => { setSelected(t); setAnswers({}); setGeneratedOrder(null); }} data-testid={`docforge-template-${t.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#D4A843] bg-[#D4A843]/10 px-2 py-0.5 rounded">{t.category}</span>
                    <span className="text-[10px] text-gray-400">SOW #{t.sow_number}</span>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1">{t.title}</h3>
                  <p className="text-[10px] text-gray-500 mb-3">{t.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-gray-900">${t.pricing.basic}<span className="text-[10px] text-gray-400 font-normal">+</span></span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Template Detail + Intake Form */}
          {view === 'store' && selected && !generatedOrder && (
            <div className="space-y-4" data-testid="docforge-intake-form">
              <Button variant="outline" size="sm" onClick={() => setSelected(null)} className="rounded-lg text-xs"><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back</Button>
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-1">{selected.title}</h2>
                <p className="text-xs text-gray-500 mb-4">{selected.description}</p>

                {/* Tier Selection */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {['basic', 'standard', 'premium'].map(tier => (
                    <div key={tier} className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${selectedTier === tier ? TIER_COLORS[tier] : 'border-gray-100'} ${selectedTier === tier ? 'bg-gray-50' : ''}`} onClick={() => setSelectedTier(tier)} data-testid={`docforge-tier-${tier}`}>
                      <p className="text-xs font-bold text-gray-900 uppercase">{tier}</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">${selected.pricing[tier]}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{tier === 'basic' ? 'Template-based, 1 revision' : tier === 'standard' ? 'Customized, 2 revisions' : 'Full package + roadmap'}</p>
                    </div>
                  ))}
                </div>

                {/* Intake Fields */}
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Intake Form</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selected.intake_fields.map(field => (
                    <div key={field.id} className={field.type === 'textarea' || field.type === 'multiselect' ? 'md:col-span-2' : ''}>
                      <label className="text-xs font-medium text-gray-700">{field.label} {field.required && <span className="text-red-500">*</span>}</label>
                      {field.type === 'text' && <Input value={answers[field.id] || ''} onChange={e => setAnswers({...answers, [field.id]: e.target.value})} className="mt-1 rounded-lg" data-testid={`docforge-field-${field.id}`} />}
                      {field.type === 'select' && (
                        <select value={answers[field.id] || ''} onChange={e => setAnswers({...answers, [field.id]: e.target.value})} className="mt-1 w-full h-9 rounded-lg border border-gray-200 px-3 text-sm bg-white" data-testid={`docforge-field-${field.id}`}>
                          <option value="">Select...</option>
                          {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      )}
                      {field.type === 'multiselect' && (
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {(field.options || []).map(o => {
                            const sel = (answers[field.id] || []).includes(o);
                            return (
                              <button key={o} type="button" onClick={() => {
                                const current = answers[field.id] || [];
                                setAnswers({...answers, [field.id]: sel ? current.filter(x => x !== o) : [...current, o]});
                              }} className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${sel ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid={`docforge-opt-${field.id}-${o.replace(/\s/g, '_')}`}>
                                {o}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {field.type === 'textarea' && <textarea value={answers[field.id] || ''} onChange={e => setAnswers({...answers, [field.id]: e.target.value})} className="mt-1 w-full h-20 rounded-lg border border-gray-200 p-3 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#D4A843]" data-testid={`docforge-field-${field.id}`} />}
                    </div>
                  ))}
                </div>

                <div className="flex gap-3 mt-6">
                  <Button onClick={handleGenerate} disabled={generating} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="docforge-generate-btn">
                    {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    {generating ? 'Generating...' : `Generate (${selectedTier} - $${selected.pricing[selectedTier]})`}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Generated Document Preview */}
          {(generatedOrder || view === 'preview') && generatedOrder && (
            <div className="space-y-4" data-testid="docforge-preview">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => { setGeneratedOrder(null); setView('store'); setSelected(null); }} className="rounded-lg text-xs"><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back</Button>
                <Button variant="outline" size="sm" onClick={() => {
                  const w = window.open('', '_blank');
                  if (w) { const safeContent = DOMPurify.sanitize(generatedOrder.content?.replace(/\n/g, '<br>') || ''); w.document.write(`<!DOCTYPE html><html><head><title>${DOMPurify.sanitize(generatedOrder.template_title)}</title><style>body{font-family:system-ui;max-width:800px;margin:40px auto;padding:20px;color:#111;line-height:1.6}h1,h2,h3{margin-top:24px}table{width:100%;border-collapse:collapse;margin:16px 0}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#f3f4f6}code{background:#f3f4f6;padding:2px 6px;border-radius:4px}footer{margin-top:40px;font-size:11px;color:#9ca3af;text-align:center;border-top:1px solid #e5e7eb;padding-top:16px}</style></head><body>${safeContent}<footer>Generated by TechOps DocForge — EIGOO Inc | eigooinc.net</footer></body></html>`); w.document.close(); }
                }} className="rounded-lg text-xs" data-testid="docforge-print"><Download className="w-3.5 h-3.5 mr-1" /> Print / PDF</Button>
              </div>
              <div className="bg-white border border-[#D4A843]/20 rounded-lg">
                <div className="bg-[#D4A843] text-[#0A0A0A] font-semibold px-6 py-4 rounded-t-lg flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold">{generatedOrder.template_title}</p>
                    <p className="text-[10px] opacity-80">{generatedOrder.tier?.toUpperCase()} | {new Date(generatedOrder.created_at).toLocaleString()}</p>
                  </div>
                  <CheckCircle2 className="w-5 h-5 text-emerald-300" />
                </div>
                <div className="p-6 prose prose-sm max-w-none text-gray-800" style={{ whiteSpace: 'pre-wrap', fontFamily: 'system-ui', fontSize: '14px', lineHeight: '1.7' }} data-testid="docforge-content">
                  {generatedOrder.content}
                </div>
              </div>
            </div>
          )}

          {/* Orders List */}
          {view === 'orders' && !generatedOrder && (
            <div className="space-y-3" data-testid="docforge-orders-list">
              {orders.length === 0 && <p className="text-center text-gray-400 py-8 bg-white rounded-lg border border-gray-200">No orders yet. Generate your first document above.</p>}
              {orders.map(o => (
                <div key={o.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between hover-lift cursor-pointer" onClick={() => viewOrder(o.id)} data-testid={`docforge-order-${o.id}`}>
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-[#D4A843]" />
                    <div>
                      <p className="text-sm font-bold text-gray-900">{o.template_title}</p>
                      <p className="text-[10px] text-gray-400">{o.tier?.toUpperCase()} | ${o.pricing} | {new Date(o.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">{o.status}</span>
                    <Eye className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
